import React, { useMemo } from "react";
import { Box, Table, Text } from "@mantine/core";
import { useSalon } from "../../../context/SalonContext";
import { SalonService } from "../../../types/salon";
import { CommissionRecord } from "../../../types/commission";
import { formatCurrency } from "../../../utils/cashDrawerUtils";
import { buildServiceRateMenu } from "./serviceRateMenu";
import styles from "./ServiceCommissionsMenu.module.css";

export interface ServiceCommissionsMenuProps {
  record: CommissionRecord;
  staffId: string;
}

/**
 * Service Commissions Menu: a service-specific rate laid out like a menu —
 * every service the staff member performs, grouped by category, with its
 * price and commission. Shown in the Commission Rate drawer's Current tab and
 * under an expanded past rate.
 */
const ServiceCommissionsMenu: React.FC<ServiceCommissionsMenuProps> = ({
  record,
  staffId,
}) => {
  const { services } = useSalon() as { services: SalonService[] | null };
  const groups = useMemo(
    () => buildServiceRateMenu(record, services || [], staffId),
    [record, services, staffId],
  );

  if (groups.length === 0) {
    return (
      <Box className={`${styles.card} ${styles.empty}`}>
        <Text size="sm" c="dimmed">
          This staff member doesn&apos;t perform any services yet.
        </Text>
      </Box>
    );
  }
  return (
    <Box className={styles.card}>
      <Table
        className={styles.table}
        withRowBorders={false}
        aria-label="Commission by service"
      >
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Service</Table.Th>
            <Table.Th className={styles.numeric}>Price</Table.Th>
            <Table.Th className={styles.numeric}>Commission</Table.Th>
          </Table.Tr>
        </Table.Thead>
        {groups.map(({ category, items }) => (
          <Table.Tbody key={category}>
            <Table.Tr className={styles.groupRow}>
              <Table.Td colSpan={3}>{category}</Table.Td>
            </Table.Tr>
            {items.map((row) => (
              <Table.Tr key={row.id}>
                <Table.Td>{row.name}</Table.Td>
                <Table.Td className={styles.numeric}>
                  {row.price != null ? formatCurrency(row.price) : "—"}
                </Table.Td>
                <Table.Td className={styles.numeric}>{row.rate}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        ))}
      </Table>
    </Box>
  );
};

export default ServiceCommissionsMenu;
